import React from 'react';

import {
	getCanvasStyle,
	getComponentMargin,
	getStyleBlock,
	getDeviceType,
} from './Style.js';

import TextComponent from './TextComponent';
import CustomComponent from './CustomComponent';
import ButtonComponent from './ButtonComponent';
import ImageComponent from './ImageComponent';
import InfogramComponent from './InfogramComponent';
import AdvertComponent from './AdvertComponent';
import DoubleAdvertComponent from './DoubleAdvertComponent';
import VideoComponent from './VideoComponent';
import DividerComponent from './DividerComponent';
import SpacerComponent from './SpacerComponent';
import GalleryComponent from './GalleryComponent';
import TwitterComponent from './TwitterComponent';
import MapComponent from './MapComponent';
import ColumnsComponent from './ColumnsComponent';

export function renderComponents({
	components,
	style,
	displayImages,
	deviceWidth,
	deviceType,
	onImageClick,
	componentStyles,
	onScaleChange,
	article,
	lang,
}) {
	const canvasStyle = getCanvasStyle(style);
	let { paddingLeft, paddingRight, canvasColor, paddingTop } = canvasStyle;

	const filteredComponents = components.filter((component) => {
		const { devices } = component;
		// Filter by devices
		if (deviceType && devices) {
			switch (deviceType) {
				case 'tablet':
					return devices?.tablet === 'on';
				case 'desktop':
					return devices?.desktop === 'on';
				case 'mobile':
					return devices?.phone === 'on';
				default:
					return true;
			}
		}

		// If the properties are missing with enable the component
		return true;
	});

	return filteredComponents
		.map((component, index) => {
			component = { ...component };
			const id = component.id ? component.id : index;
			if (article) {
				component.article = { ...article };
			}

			switch (component.component) {
				case 'headline':
				case 'title':
				case 'subtitle':
				case 'intro':
				case 'body':
				case 'crosshead':
				case 'byline':
				case 'blockquote':
				case 'text1':
				case 'text2':
				case 'text3':
				case 'text4':
				case 'text5':
				case 'text6':
				case 'text7':
				case 'text8':
				case 'text9':
				case 'text10':
				case 'footer':
					return (
						<TextComponent
							key={id}
							component={component}
							lang={lang}
							style={style}
							stylesMap={componentStyles}
						/>
					);
				case 'map':
					return (
						<MapComponent
							key={id}
							mediaStyle={style['canvas']}
							component={component}
							id={component.id}
						/>
					);
				case 'button':
					return (
						<ButtonComponent
							key={id}
							id={id}
							component={component}
							style={style['buttons']}
						/>
					);
				case 'twitter':
					return (
						<TwitterComponent
							key={id}
							component={component}
							name={component.name}
						/>
					);
				case 'divider':
					return (
						<DividerComponent
							key={id}
							component={component}
							style={style}
						/>
					);
				case 'infogram':
					return <InfogramComponent key={id} component={component} />;
				case 'custom':
					return <CustomComponent component={component} />;
				case 'spacer':
					return (
						<SpacerComponent
							key={id}
							component={component}
							style={style}
							stylesMap={componentStyles}
						/>
					);
				case 'advert':
					if (!displayImages) {
						return null;
					}

					return (
						<AdvertComponent
							key={id}
							onImageClick={onImageClick}
							deviceWidth={deviceWidth}
							component={component}
							readerMode={false}
							onScaleChange={onScaleChange}
						/>
					);
				case 'double-advert':
					if (!displayImages) {
						return null;
					}

					return (
						<DoubleAdvertComponent
							key={id}
							onImageClick={onImageClick}
							deviceWidth={deviceWidth}
							component={component}
							readerMode={false}
							onScaleChange={onScaleChange}
						/>
					);
				case 'image':
					if (!displayImages) {
						return null;
					}

					return (
						<ImageComponent
							key={id}
							onImageClick={onImageClick}
							deviceWidth={deviceWidth}
							component={component}
							mediaStyle={style?.canvas}
							onScaleChange={onScaleChange}
							readerMode={false}
							style={style}
						/>
					);
				case 'video':
					return (
						<VideoComponent
							mediaStyle={style?.canvas}
							key={id}
							component={component}
						/>
					);
				case 'gallery':
					if (!displayImages) {
						return null;
					}

					return (
						<GalleryComponent
							key={id}
							lang={lang}
							images={component.images}
							mediaStyle={style?.canvas}
							component={component}
							readerMode={false}
						/>
					);
				case 'columns':
					if (!!component.styles && !!componentStyles) {
						component.styles = component.styles
							.map((s) => {
								if (
									typeof s === 'string' ||
									typeof s === 'number'
								) {
									return componentStyles[`${s}`];
								}

								if (typeof s === 'object') {
									return s;
								}
								return null;
							})
							.filter((s) => !!s);
						component.columns = component.columns.map((column) => {
							return column.map((c) => {
								c.styles = component.styles;
								return c;
							});
						});
					}

					return (
						<ColumnsComponent
							key={id}
							lang={lang}
							onScaleChange={onScaleChange}
							styleblock={getStyleBlock({
								id: component.styleblock,
								style,
							})}
							deviceWidth={deviceWidth}
							onImageClick={onImageClick}
							component={component}
							style={style}
							readerMode={false}
						/>
					);
				default:
					return null;
			}
		})
		.map((component, i) => {
			let width = '100%';
			let justifyContent = 'center';
			if (!component) {
				return null;
			}
			const compData = filteredComponents[i];
			const { id } = compData;

			const { marginBottom, marginTop } = getComponentMargin({
				component: compData,
				style,
			});

			const componentStyle = {
				paddingLeft,
				paddingRight,
				paddingBottom: marginBottom,
				paddingTop: marginTop,
				maxWidth: 1024,
				backgroundColor: canvasColor,
				width,
			};

			if (compData.align === 'left') {
				componentStyle.justifyContent = 'flex-start';
			} else if (compData.align === 'right') {
				componentStyle.justifyContent = 'flex-end';
			} else {
				componentStyle.justifyContent = 'center';
			}

			if (compData.component === 'columns') {
				if (getDeviceType({ deviceWidth }) === 'mobile') {
					componentStyle.width = `100%`;
				} else {
					if (compData.contentmode !== 'multicol') {
						componentStyle.display = 'flex';
					}
				}
			}

			if (compData.expandfullwidth === 'on') {
				componentStyle.width = '100vw';
				componentStyle.paddingLeft = 0;
				componentStyle.paddingRight = 0;
				componentStyle.maxWidth = undefined;
			}

			/*if (
				compData.component !== 'image' &&
				compData.component !== 'video' &&
				compData.component !== 'gallery' &&
				compData.component !== 'map'
			) {
				componentStyle.marginBottom = 0;
			}*/

			if (compData.bleed === 'on') {
				componentStyle.paddingLeft = 0;
				componentStyle.paddingRight = 0;
			}

			if (compData.fullwidth === 'on') {
				componentStyle.paddingLeft = 0;
				componentStyle.paddingRight = 0;
			}

			if (compData['component'] === 'double-advert') {
				componentStyle.maxWidth = 1600;
				componentStyle.display = 'flex';
				componentStyle.justifyContent = 'center';
				componentStyle.alignItems = 'center';
			}

			// Apply top padding
			if (i == 0 && paddingTop) {
				componentStyle.paddingTop = paddingTop;
			}

			return (
				<div
					key={i}
					id={`container-${id}`}
					style={{
						width: '100%',
						display: 'flex',
						justifyContent: justifyContent,
					}}
					background={null}
				>
					<div style={componentStyle}>
						{component}
					</div>
				</div>
			);
		});
}

export function renderColumnComponents({
	components,
	displayImages,
	style,
	deviceWidth,
	onImageClick,
	styleblock,
	onScaleChange,
	lang,
}) {
	if (!style) {
		style = {};
	}

	return components
		.map((component, index) => {
			let id = component.id ? component.id : index;

			switch (component.component) {
				case 'headline':
				case 'title':
				case 'subtitle':
				case 'intro':
				case 'body':
				case 'crosshead':
				case 'byline':
				case 'blockquote':
				case 'text1':
				case 'text2':
				case 'text3':
				case 'text4':
				case 'text5':
				case 'text6':
				case 'text7':
				case 'text8':
				case 'text9':
				case 'text10':
				case 'footer':
					return (
						<TextComponent
							key={id}
							lang={lang}
							component={component}
							styleblock={styleblock}
							style={{ ...style }}
						/>
					);
				case 'map':
					return (
						<MapComponent
							key={id}
							mediaStyle={style['canvas']}
							component={component}
							id={component.id}
						/>
					);
				case 'infogram':
					return <InfogramComponent key={id} component={component} />;
				case 'button':
					return (
						<ButtonComponent
							key={id}
							id={id}
							component={component}
							style={style['buttons']}
						/>
					);
				case 'divider':
					return (
						<DividerComponent
							key={id}
							component={component}
							style={style}
						/>
					);
				case 'spacer':
					return (
						<SpacerComponent
							key={id}
							component={component}
							style={{ ...style }}
						/>
					);
				case 'custom':
					return <CustomComponent component={component} />;
				case 'advert':
					if (!displayImages) {
						return null;
					}

					return (
						<AdvertComponent
							key={id}
							onImageClick={onImageClick}
							deviceWidth={deviceWidth}
							component={component}
							readerMode={false}
						/>
					);
				case 'double-advert':
					if (!displayImages) {
						return null;
					}

					return (
						<DoubleAdvertComponent
							key={id}
							onImageClick={onImageClick}
							deviceWidth={deviceWidth}
							component={component}
							readerMode={false}
							onScaleChange={onScaleChange}
						/>
					);
				case 'image':
					if (!displayImages) {
						return null;
					}

					return (
						<ImageComponent
							key={id}
							onImageClick={onImageClick}
							deviceWidth={deviceWidth}
							component={component}
							readerMode={false}
							style={style}
							mediaStyle={style['canvas']}
						/>
					);
				case 'video':
					return (
						<VideoComponent
							key={id}
							mediaStyle={style['canvas']}
							component={component}
						/>
					);
				case 'gallery':
					if (!displayImages) {
						return null;
					}

					return (
						<GalleryComponent
							key={id}
							lang={lang}
							mediaStyle={style['canvas']}
							images={component.images}
							component={component}
							readerMode={false}
						/>
					);
				default:
					return null;
			}
		})
		.filter((component) => !!component)
		.map((component, i) => {
			const { marginBottom } = getComponentMargin({
				component: components[i],
				style,
			});
			return (
				<div
					key={i}
					id={components[i].id}
					style={{
						width: '100%',
						display: 'flex',
						justifyContent: 'center',
						marginBottom,
					}}
				>
					{component}
				</div>
			);
		});
}
